"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.removeTerraDraw = exports.getTerraDraw = void 0;
const terra_draw_1 = require("terra-draw");
const terra_draw_maplibre_gl_adapter_1 = require("terra-draw-maplibre-gl-adapter");
const react_1 = __importDefault(require("react"));
const client_1 = require("react-dom/client");
const react_fontawesome_1 = require("@fortawesome/react-fontawesome");
const free_solid_svg_icons_1 = require("@fortawesome/free-solid-svg-icons");
/**
 * Creates an empty polygon feature used as a sentinel value when clearing selections.
 * Used to signal deletion to callbacks without having actual polygon data.
 */
const createEmptyPolygonFeature = () => ({
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [] }
});
// Store draw instances associated with the map to ensure we can clean up any existing instance
// regardless of the adapter wrapper reference. Use a Map<MapLibreGLMap, TerraDraw>
const mapDrawInstances = new WeakMap();
const mapControls = new WeakMap();
class TerraDrawControl {
    constructor(draw, modeChangeCallback, deleteCallback) {
        this._draw = draw;
        this._modeChangeCallback = modeChangeCallback;
        this._deleteCallback = deleteCallback;
    }
    reset(force = false) {
        // Toggle off polygon mode if active, similar to clicking the button
        if (this._draw.getMode() === 'polygon' || force) {
            this._draw.setMode('select');
            this._modeChangeCallback({ mode: 'simple_select' });
            if (this._polygonButton) {
                this._polygonButton.classList.remove('active');
            }
        }
    }
    deleteAll() {
        try {
            this._draw.clear();
            // Manually trigger deletion callback (MainMap expects this to clear selection)
            this._deleteCallback(createEmptyPolygonFeature());
            // Updating IDs will hide the trash button
            this.updateIds([]);
            // Also reset mode
            this.reset();
        }
        catch (e) {
            console.error('Error deleting features', e);
        }
    }
    onAdd(map) {
        this._map = map;
        this._container = document.createElement('div');
        this._container.className = 'maplibregl-ctrl maplibregl-ctrl-group';
        // Polygon Button
        this._polygonButton = document.createElement('button');
        this._polygonButton.className = 'maplibregl-ctrl-icon terra-draw_ctrl-draw-btn';
        this._polygonButton.type = 'button';
        this._polygonButton.title = 'Polygon tool';
        // Flex center the icon
        this._polygonButton.style.display = 'flex';
        this._polygonButton.style.justifyContent = 'center';
        this._polygonButton.style.alignItems = 'center';
        this._polygonRoot = (0, client_1.createRoot)(this._polygonButton);
        this._polygonRoot.render(react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faDrawPolygon, style: { fontSize: '16px' } }));
        this._polygonButton.onclick = () => {
            if (this._draw.getMode() === 'polygon') {
                this.reset(true);
            }
            else {
                this._draw.setMode('polygon');
                this._modeChangeCallback({ mode: 'draw_polygon' });
                this._polygonButton.classList.add('active');
            }
        };
        this._container.appendChild(this._polygonButton);
        // Trash Button
        this._trashButton = document.createElement('button');
        this._trashButton.className = 'maplibregl-ctrl-icon terra-draw_ctrl-draw-btn';
        this._trashButton.type = 'button';
        this._trashButton.style.display = 'none'; // Hidden by default
        this._trashButton.style.justifyContent = 'center';
        this._trashButton.style.alignItems = 'center';
        this._trashButton.title = 'Delete';
        this._trashRoot = (0, client_1.createRoot)(this._trashButton);
        this._trashRoot.render(react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faTrashCan, style: { fontSize: '16px' } }));
        this._trashButton.onclick = () => {
            this.deleteAll();
        };
        this._container.appendChild(this._trashButton);
        return this._container;
    }
    onRemove() {
        var _a, _b, _c;
        // Unmount React roots to avoid memory leaks
        (_a = this._polygonRoot) === null || _a === void 0 ? void 0 : _a.unmount();
        this._polygonRoot = undefined;
        (_b = this._trashRoot) === null || _b === void 0 ? void 0 : _b.unmount();
        this._trashRoot = undefined;
        (_c = this._container.parentNode) === null || _c === void 0 ? void 0 : _c.removeChild(this._container);
        this._map = undefined;
    }
    updateIds(selectedIds) {
        // Toggle trash button visibility and polygon button state
        const hasFeatures = this._draw.getSnapshot().length > 0;
        if (selectedIds.length > 0 || hasFeatures) {
            this._trashButton.style.display = 'flex';
        }
        else {
            this._trashButton.style.display = 'none';
        }
    }
}
const cleanUpTerraDrawSources = (map) => {
    // Manually remove TerraDraw sources/layers if they were left behind
    const style = map.getStyle();
    if (!style)
        return;
    if (style.layers) {
        style.layers.forEach((layer) => {
            // TerraDraw usually prefixes with td- or terra-draw-
            // Error said "td-polygon"
            if (layer.id.startsWith('td-') || layer.id.startsWith('terra-draw')) {
                try {
                    if (map.getLayer(layer.id))
                        map.removeLayer(layer.id);
                }
                catch (e) {
                    console.warn(`Failed to remove layer ${layer.id}`, e);
                }
            }
        });
    }
    if (style.sources) {
        Object.keys(style.sources).forEach((sourceId) => {
            if (sourceId.startsWith('td-') || sourceId.startsWith('terra-draw')) {
                try {
                    if (map.getSource(sourceId))
                        map.removeSource(sourceId);
                }
                catch (e) {
                    console.warn(`Failed to remove source ${sourceId}`, e);
                }
            }
        });
    }
};
const getTerraDraw = (map, modeChangeCallback, createCallback, deleteCallback, _updateCallback) => {
    // Ensure any existing instance on this map is cleaned up first
    if (mapDrawInstances.has(map)) {
        const existingDraw = mapDrawInstances.get(map);
        if (existingDraw) {
            try {
                // Try stop() first
                existingDraw.stop();
            }
            catch (e) {
                console.error('Error stopping existing TerraDraw instance', e);
            }
        }
        const existingControl = mapControls.get(map);
        if (existingControl) {
            try {
                map.removeControl(existingControl);
            }
            catch (e) {
                console.error('Error removing existing TerraDraw control', e);
                // Ignore if already removed
            }
        }
        mapDrawInstances.delete(map);
        mapControls.delete(map);
    }
    // Force manual cleanup of sources/layers to prevent "Source already exists"
    cleanUpTerraDrawSources(map);
    // Initialize TerraDraw
    const draw = new terra_draw_1.TerraDraw({
        adapter: new terra_draw_maplibre_gl_adapter_1.TerraDrawMapLibreGLAdapter({ map }),
        modes: [
            new terra_draw_1.TerraDrawSelectMode({
                flags: {
                    polygon: {
                        feature: {
                            draggable: true,
                            rotateable: true,
                            scaleable: true,
                            coordinates: {
                                midpoints: true,
                                draggable: true,
                                deletable: true
                            }
                        }
                    }
                },
                styles: {
                    selectedPolygonColor: '#ff9900',
                    selectedPolygonFillOpacity: 0.1,
                    selectedPolygonOutlineColor: '#ff9900',
                    selectedPolygonOutlineWidth: 2,
                    selectionPointColor: '#ff9900',
                    selectionPointWidth: 6, // Smaller nodes
                    selectionPointOutlineColor: '#ffffff',
                    selectionPointOutlineWidth: 2
                }
            }),
            new terra_draw_1.TerraDrawPolygonMode({
                styles: {
                    fillColor: '#ff9900',
                    fillOpacity: 0.1,
                    outlineColor: '#ff9900',
                    outlineWidth: 2,
                    closingPointColor: '#ff9900',
                    closingPointWidth: 6, // Smaller nodes
                    closingPointOutlineColor: '#ffffff',
                    closingPointOutlineWidth: 2,
                    coordinatePointColor: '#ff9900',
                    coordinatePointWidth: 6,
                    coordinatePointOutlineColor: '#ffffff',
                    coordinatePointOutlineWidth: 2
                }
            })
        ]
    });
    try {
        draw.start();
    }
    catch (e) {
        console.error('Error starting TerraDraw:', e);
        // If start fails, cleanup and abort initialization to avoid registering
        // handlers/controls against a failed TerraDraw instance
        cleanUpTerraDrawSources(map);
        console.error('TerraDraw failed to start');
        return undefined;
    }
    // Create custom control
    const control = new TerraDrawControl(draw, modeChangeCallback, deleteCallback);
    map.addControl(control, 'top-right');
    mapDrawInstances.set(map, draw);
    mapControls.set(map, control);
    const updateControl = () => {
        control.updateIds([]);
    };
    const getAggregateFeature = () => {
        const snapshot = draw.getSnapshot();
        // Filter out invalid/incomplete polygons (e.g. drawing in progress or glitch)
        const validPolygons = snapshot.filter((f) => f.geometry &&
            f.geometry.coordinates &&
            f.geometry.coordinates.length > 0 &&
            f.geometry.coordinates[0].length >= 4);
        if (validPolygons.length === 0)
            return null;
        if (validPolygons.length === 1) {
            return validPolygons[0];
        }
        return {
            type: 'FeatureCollection',
            features: validPolygons
        };
    };
    // Event Listeners
    draw.on('change', () => {
        // Detect mode. If 'polygon', we are drawing. Don't spam createCallback which triggers full refresh/add/delete in MainMap.
        const mode = draw.getMode();
        if (mode === 'polygon') {
            return;
        }
        const feature = getAggregateFeature();
        if (feature) {
            createCallback(feature);
            updateControl();
        }
        else {
            // Only delete if there are truly no features in the snapshot
            // This prevents clearing selection if we have an "invalid" polygon state or transient state
            const snapshot = draw.getSnapshot();
            if (snapshot.length === 0) {
                deleteCallback(createEmptyPolygonFeature());
                updateControl();
            }
        }
    });
    // Listen to finish event
    draw.on('finish', () => {
        draw.setMode('polygon');
        // Notify external listeners of mode change
        modeChangeCallback({ mode: 'draw_polygon' });
        // Update control button state - ensure it stays active
        if (control._polygonButton) {
            control._polygonButton.classList.add('active');
        }
        // Trigger creation immediately with the new aggregated set
        const feature = getAggregateFeature();
        if (feature) {
            createCallback(feature);
            updateControl();
        }
    });
    // Mock the TerraDrawAdapter interface expected by TransitionMainMap
    const drawWrapper = {
        getAll: () => {
            const features = draw.getSnapshot();
            return {
                type: 'FeatureCollection',
                features: features
            };
        },
        deleteAll: () => {
            // Use the control method to ensure specific cleanup logic (reset, UI update) is handled
            control.deleteAll();
            return drawWrapper;
        },
        add: (geojson) => {
            // Support adding features back to TerraDraw
            if (geojson.type === 'FeatureCollection') {
                draw.addFeatures(geojson.features);
                return geojson.features.map((f) => f.id || '');
            }
            else {
                draw.addFeatures([geojson]);
                return [geojson.id || ''];
            }
        },
        reset: () => {
            control.reset();
        },
        onRemove: () => {
            try {
                draw.stop();
            }
            catch (e) {
                console.error('Error stopping TerraDraw', e);
            }
            try {
                map.removeControl(control);
            }
            catch (e) {
                console.error('Error removing TerraDraw control', e);
                // Control might already be removed
            }
            // Explicitly run cleanup too, just in case
            cleanUpTerraDrawSources(map);
            mapDrawInstances.delete(map);
            mapControls.delete(map);
        }
    };
    return drawWrapper;
};
exports.getTerraDraw = getTerraDraw;
const removeTerraDraw = (map, drawWrapper) => {
    // Try to remove using the wrapper callback
    if (drawWrapper && typeof drawWrapper.onRemove === 'function') {
        drawWrapper.onRemove();
    }
    // Explicitly check map instance map to ensure cleanup
    if (mapDrawInstances.has(map)) {
        const draw = mapDrawInstances.get(map);
        if (draw) {
            try {
                draw.stop();
            }
            catch (e) {
                console.error('Error stopping TerraDraw (force cleanup)', e);
            }
        }
        cleanUpTerraDrawSources(map);
        mapDrawInstances.delete(map);
        mapControls.delete(map);
    }
};
exports.removeTerraDraw = removeTerraDraw;
//# sourceMappingURL=MapPolygonService.js.map